import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ReportShell } from "@/features/reports/ReportShell";
import { useAssets } from "@/features/assets/api";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";
import type { ReportFilters } from "@/features/reports/types";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/reports/asset-register")({
  component: AssetRegisterReport,
});

function AssetRegisterReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? filters.branchId ?? currentBranchId ?? null;
  const { data, isLoading } = useAssets({
    branchId: effectiveBranchId,
    departmentId: filters.departmentId ?? null,
    categoryId: filters.categoryId ?? null,
    criticality: filters.criticality ?? null,
    status: filters.status ?? null,
    assetClass: filters.assetClass ?? null,
    assetType: filters.assetType ?? null,
    page: 1, pageSize: 500,
  });
  const rows = data?.rows ?? [];

  const procSource = (r: (typeof rows)[number]): string => {
    const anyR = r as unknown as { source_grn_id?: string | null; po_number?: string | null };
    if (anyR.source_grn_id) return "GRN";
    if (anyR.po_number) return "Manual (PO)";
    return "Manual";
  };

  const exportRows = useMemo(() => () => rows.map((r) => {
    const anyR = r as unknown as {
      po_number?: string | null; invoice_number?: string | null; invoice_date?: string | null;
      capitalisation_status?: string | null; basic_cost?: number | null; source_grn_id?: string | null;
    };
    return {
      Code: r.asset_code,
      Name: r.name,
      Class: r.asset_class ?? "Equipment",
      Type: r.asset_type ?? "",
      Category: r.category?.name ?? "",
      Branch: r.branch?.name ?? "",
      Department: r.department?.name ?? "",
      Location: r.location?.name ?? "",
      Status: r.status,
      Criticality: r.criticality ?? "",
      Make: r.make ?? "",
      Model: r.model ?? "",
      Serial: r.serial_number ?? "",
      VehicleRegNo: r.vehicle_registration_number ?? "",
      BuildingName: r.building_name ?? "",
      PONumber: anyR.po_number ?? "",
      GRNLinked: anyR.source_grn_id ? "Yes" : "",
      Vendor: r.supplier?.name ?? "",
      InvoiceNumber: anyR.invoice_number ?? "",
      InvoiceDate: anyR.invoice_date ?? "",
      PurchasePrice: anyR.basic_cost ?? "",
      ProcurementSource: procSource(r),
      CapitalisationStatus: anyR.capitalisation_status ?? "",
      PurchaseCost: r.total_capitalised_value ?? "",
      InstallationDate: r.installation_date ?? "",
    };
  }), [rows]);

  return (
    <ReportShell
      id="R-01" title="Asset Register"
      description="Master list of assets across branches with class, category, custodian and status."
      filters={filters} onChange={setFilters}
      enabled={["branchId", "departmentId", "categoryId", "criticality", "status", "assetClass", "assetType"]}
      exportRows={exportRows}
    >
      <Card>
        <CardContent className="p-0">
          {isLoading ? <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Code</TableHead><TableHead>Name</TableHead>
                <TableHead>Class</TableHead><TableHead>Category</TableHead>
                <TableHead>Branch</TableHead><TableHead>Dept</TableHead><TableHead>Status</TableHead>
                <TableHead>PO #</TableHead><TableHead>Vendor</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead className="text-right">Purchase ₹</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Capitalisation</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const anyR = r as unknown as {
                    po_number?: string | null; invoice_number?: string | null; invoice_date?: string | null;
                    capitalisation_status?: string | null; basic_cost?: number | null;
                  };
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">
                        {r.asset_code}
                        {r.vehicle_registration_number && (
                          <div className="text-[10px] text-muted-foreground">{r.vehicle_registration_number}</div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell><Badge variant="secondary">{r.asset_class ?? "Equipment"}</Badge></TableCell>
                      <TableCell>{r.category?.name ?? "—"}</TableCell>
                      <TableCell>{r.branch?.name ?? "—"}</TableCell>
                      <TableCell>{r.department?.name ?? "—"}</TableCell>
                      <TableCell><Badge variant="secondary">{r.status}</Badge></TableCell>
                      <TableCell className="font-mono text-xs">{anyR.po_number ?? "—"}</TableCell>
                      <TableCell>{r.supplier?.name ?? "—"}</TableCell>
                      <TableCell className="text-xs">
                        {anyR.invoice_number ?? "—"}
                        {anyR.invoice_date && <div className="text-muted-foreground">{anyR.invoice_date}</div>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{anyR.basic_cost != null ? Number(anyR.basic_cost).toFixed(2) : "—"}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{procSource(r)}</Badge></TableCell>
                      <TableCell className="text-xs">{anyR.capitalisation_status ?? "—"}</TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && <TableRow><TableCell colSpan={13} className="py-8 text-center text-sm text-muted-foreground">No assets match these filters.</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
          <div className="border-t p-3 text-xs text-muted-foreground">Showing {rows.length} of {data?.total ?? 0} assets (first 500).</div>
        </CardContent>
      </Card>
    </ReportShell>
  );
}
